import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MEDIA } from "@/constants/media";
import { ADDRESS } from "@/i18n/translations";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useSite();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(c.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.success);
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "rounded-none border-border bg-background/60 font-body focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold";

  return (
    <section id="contact" data-testid="contact-section" className="relative grid grid-cols-1 lg:grid-cols-2">
      {/* Left — form */}
      <div className="bg-background px-6 py-24 lg:px-16 lg:py-32">
        <div className="mx-auto max-w-xl">
          <Reveal>
            <Overline>{c.overline}</Overline>
            <h2 className="mt-6 font-heading text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              {c.title}
            </h2>
            <p className="mt-6 font-body text-base font-light text-muted-foreground">{c.subtitle}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="mt-10 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                    {c.name}
                  </Label>
                  <Input data-testid="contact-name" value={form.name} onChange={update("name")} className={inputCls} required />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                    {c.email}
                  </Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={inputCls} required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                    {c.company}
                  </Label>
                  <Input data-testid="contact-company" value={form.company} onChange={update("company")} className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                    {c.subject}
                  </Label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  {c.message}
                </Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} className={inputCls} required />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-body text-sm font-medium uppercase tracking-[0.12em] text-black transition-opacity hover:opacity-85 disabled:opacity-50 sm:w-auto"
              >
                {loading ? c.submitting : c.submit}
                {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      {/* Right — address over darkened skyline */}
      <div className="relative min-h-[420px] overflow-hidden">
        <img src={MEDIA.office} alt="Jakarta skyline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 flex h-full flex-col justify-center px-6 py-24 lg:px-16">
          <Reveal>
            <div className="flex items-center gap-3 text-gold">
              <MapPin size={20} />
              <span className="font-body text-xs font-medium uppercase tracking-luxe">
                {c.addressLabel}
              </span>
            </div>
            <p className="mt-8 max-w-md font-heading text-2xl font-light leading-snug text-white sm:text-3xl">
              {ADDRESS}
            </p>
            <div className="mt-10 h-px w-16 bg-gold" />
            <p className="mt-6 font-body text-sm font-light text-white/60">
              PT MAHAKAIN SUTERA NIRMALA
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
